import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Users, FileText, Download, Calendar, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = () => {
  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 5); // Default: last 6 months
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [stats, setStats] = useState({
    totalRevenue: 0,
    paidInvoices: 0,
    unpaidInvoices: 0,
    totalCustomers: 0,
    revenueGrowth: 0
  });
  const [revenueByMonth, setRevenueByMonth] = useState([]);
  const [revenueByBranch, setRevenueByBranch] = useState([]);
  const [customerGrowth, setCustomerGrowth] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [startDate, endDate]);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build query params for date range
      const params = new URLSearchParams({
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString()
      });

      // Fetch all analytics data from new endpoints
      const [summaryRes, trendRes, branchRes, growthRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/revenue-summary?${params}`, { headers }),
        axios.get(`${API_URL}/api/analytics/revenue-trend?${params}`, { headers }),
        axios.get(`${API_URL}/api/analytics/revenue-by-branch?${params}`, { headers }),
        axios.get(`${API_URL}/api/analytics/customer-growth?${params}`, { headers }),
        axios.get(`${API_URL}/api/analytics/payment-history?${params}&limit=10`, { headers })
      ]);

      // Set stats from summary
      const summary = summaryRes.data.data;
      setStats({
        totalRevenue: summary.total_revenue || 0,
        paidInvoices: summary.paid_invoices || 0,
        unpaidInvoices: summary.unpaid_invoices || 0,
        totalCustomers: summary.total_customers || 0,
        revenueGrowth: summary.revenue_growth || 0
      });

      // Set revenue trend
      setRevenueByMonth(trendRes.data.data || []);

      // Set revenue by branch
      setRevenueByBranch(branchRes.data.data || []);

      // Set customer growth
      setCustomerGrowth(growthRes.data.data || []);

      // Set payment history (nested data structure)
      setPaymentHistory(historyRes.data.data?.data || []);

    } catch (error) {
      toast.error('Gagal memuat data analytics');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Quick date range presets
  const handleQuickRange = (range) => {
    const end = new Date();
    let start = new Date();
    
    switch(range) {
      case 'today':
        start = new Date();
        break;
      case 'week':
        start.setDate(end.getDate() - 7);
        break;
      case 'month':
        start.setMonth(end.getMonth() - 1);
        break;
      case '3months':
        start.setMonth(end.getMonth() - 3);
        break;
      case '6months':
        start.setMonth(end.getMonth() - 6);
        break;
      case 'year':
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        break;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const handleExport = (type) => {
    toast.success(`Export ${type} akan segera diunduh`);
    // TODO: Implement actual export
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const COLORS = ['#10b981', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 dark:text-slate-200">
              <TrendingUp className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
              Analytics & Reports
            </h1>
            <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
              Revenue analytics, customer growth, dan payment reports
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={() => handleExport('Excel')}
              variant="outline"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button
              onClick={() => handleExport('PDF')}
              variant="outline"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>

        {/* Date Range Filter */}
        <Card className="border-0 shadow-md bg-white dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Periode:</span>
              </div>
              
              {/* Quick Range Buttons */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => handleQuickRange('week')}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  7 Hari
                </Button>
                <Button
                  onClick={() => handleQuickRange('month')}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  1 Bulan
                </Button>
                <Button
                  onClick={() => handleQuickRange('3months')}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  3 Bulan
                </Button>
                <Button
                  onClick={() => handleQuickRange('6months')}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  6 Bulan
                </Button>
                <Button
                  onClick={() => handleQuickRange('year')}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  1 Tahun
                </Button>
              </div>
              
              {/* Custom Date Inputs */}
              <div className="flex items-center gap-2 ml-auto">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                />
                <span className="text-slate-500 dark:text-slate-400">-</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                />
                <Button
                  onClick={fetchAnalytics}
                  size="sm"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-20">
                <RefreshCw className="w-5 h-5 text-green-600 animate-spin dark:text-green-400" />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600 font-medium dark:text-slate-300">Total Revenue</p>
                  <p className="text-xl font-bold text-green-600 mt-1 dark:text-green-400">
                    {formatCurrency(stats.totalRevenue)}
                  </p>
                  <p className="text-xs text-green-600 mt-1 dark:text-green-400">
                    {stats.revenueGrowth >= 0 ? '↑' : '↓'} {Math.abs(stats.revenueGrowth).toFixed(1)}% vs previous
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-20">
                <RefreshCw className="w-5 h-5 text-blue-600 animate-spin dark:text-blue-400" />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600 font-medium dark:text-slate-300">Paid Invoices</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1 dark:text-blue-400">{stats.paidInvoices}</p>
                </div>
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-orange-50 to-amber-50">
          <CardContent className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-20">
                <RefreshCw className="w-5 h-5 text-orange-600 animate-spin dark:text-orange-400" />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600 font-medium dark:text-slate-300">Unpaid Invoices</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1 dark:text-orange-400">{stats.unpaidInvoices}</p>
                </div>
                <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-pink-50">
          <CardContent className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-20">
                <RefreshCw className="w-5 h-5 text-purple-600 animate-spin dark:text-purple-400" />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600 font-medium dark:text-slate-300">Total Customers</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1 dark:text-purple-400">{stats.totalCustomers}</p>
                </div>
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-700">
            <CardTitle className="text-lg">Revenue Trend (6 Months)</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={revenueByMonth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" style={{fontSize: '12px'}} />
                <YAxis style={{fontSize: '12px'}} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Customer Growth Chart */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-700">
            <CardTitle className="text-lg">Customer Growth (6 Months)</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={customerGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" style={{fontSize: '12px'}} />
                <YAxis style={{fontSize: '12px'}} />
                <Tooltip />
                <Bar dataKey="customers" fill="#6366f1" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Branch */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-700">
            <CardTitle className="text-lg">Revenue per Cabang</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row items-center gap-4">
              <div className="w-full lg:w-3/5">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={revenueByBranch}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="revenue"
                      label={({ name, revenue }) => `${name}: ${formatCurrency(revenue)}`}
                      style={{fontSize: '10px'}}
                    >
                      {revenueByBranch.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-2/5 space-y-2">
                {revenueByBranch.map((branch, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{backgroundColor: COLORS[index % COLORS.length]}}
                    ></div>
                    <span className="text-sm text-slate-700 dark:text-slate-200">{branch.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-700">
            <CardTitle className="text-lg">Recent Payments</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {paymentHistory.map((payment, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700">
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{payment.customer_name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{payment.invoice_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(payment.total)}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(payment.payment_date || payment.updated_at).toLocaleDateString('id-ID')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
